import { useState } from "react";
import { Button } from "./ui/Button";
import { aiAssist, type AssistKind } from "../lib/ai";

/**
 * A "Draft with AI" affordance for a text field. Sends the current text as notes to the
 * AI gateway and calls `onResult` with the drafted copy. Degrades gracefully: when the
 * gateway is unconfigured (501) it shows an inline note instead of failing loudly.
 */
export function AiAssist({ kind, value, onResult, label = "Draft with AI" }: {
  kind: AssistKind;
  value: string;
  onResult: (text: string) => void;
  label?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function run() {
    if (value.trim().length < 3) { setNote("Add a few notes first, then let AI polish them."); return; }
    setBusy(true);
    setNote(null);
    try {
      const { text } = await aiAssist(kind, value);
      onResult(text);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      setNote(/not configured|ai_unconfigured|501/i.test(msg) ? "AI assist isn't enabled yet." : "Couldn't reach AI — try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button type="button" variant="ghost" onClick={run} disabled={busy} data-testid="ai-assist">
        {busy ? "Drafting…" : `✨ ${label}`}
      </Button>
      {note && <span className="font-mono text-[11px] text-muted" data-testid="ai-assist-note">{note}</span>}
    </div>
  );
}
